import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CURRENCY_TYPE_LIST } from '../config/constants';

const useProductSearchFilter = () => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const filterSearchParams = new URLSearchParams(search);
  const searchParam = filterSearchParams.get('search');
  const locationParam = filterSearchParams.get('location');
  const categoryParam = filterSearchParams.get('category');
  const vendorParam = filterSearchParams.get('vendor');
  const sortParam = filterSearchParams.get('sort');
  const minPriceParam = filterSearchParams.get('minPrice');
  const maxPriceParam = filterSearchParams.get('maxPrice');
  const pageIndexParam = filterSearchParams.get('page');

  const filter = {
    search: searchParam ?? '',
    location: locationParam ?? null,
    category: categoryParam ?? null,
    vendor: vendorParam ?? null,
    sort: sortParam ?? null,
    minPrice: minPriceParam ?? null,
    maxPrice: maxPriceParam ?? null
  };

  const hasSearched =
    !!filter.search ||
    !!filter.location ||
    !!filter.category ||
    !!filter.vendor ||
    !!filter.sort ||
    !!filter.minPrice ||
    !!filter.maxPrice;

  const resetFilter = () => {
    navigate(pathname, { replace: true });
  };

  const objectToParams = (obj) => {
    const params = new URLSearchParams();
    Object.keys(obj).forEach((key) => {
      if (obj[key]) {
        params.set(key, obj[key]);
      }
    });
    return params.toString();
  };

  const onFilterChange = (params) => {
    const newParams = objectToParams({ ...filter, ...params });
    navigate(`${pathname}?${newParams}`, { replace: true });
  };

  return {
    filter,
    hasSearched,
    resetFilter,
    onFilterChange,
    pageIndex: pageIndexParam ? parseInt(pageIndexParam, 10) : 1
  };
};

const getVendorBranding = (ecomVendor) => ({
  primaryColor: ecomVendor?.primaryColor ?? '#FFF',
  primaryContrastColor: ecomVendor?.primaryColor
    ? ecomVendor?.primaryContrastColor ?? '#000'
    : '#000',
  secondaryColor: ecomVendor?.secondaryColor ?? '#000',
  secondaryAltColor: ecomVendor?.secondaryAltColor ?? '#FFF',
  secondaryContrastColor: ecomVendor?.secondaryColor
    ? ecomVendor?.secondaryContrastColor ?? '#000'
    : '#000'
});

const useCatalogBackPath = (baseUrl) => {
  const { search } = useLocation();
  const { ecomVendor } = useParams();
  const searchParams = new URLSearchParams(search);
  const fromLocations = searchParams.get('from')?.split(',') || [];
  const fromId = searchParams.get('fromId');

  const getBackPath = () => {
    const routes = {
      vendor: `${baseUrl}/vendors`,
      'vendor-product': `${baseUrl}/products/${ecomVendor}`,
      products: `${baseUrl}/products`,
      categories: `${baseUrl}/categories`
    };

    const params =
      fromLocations.length > 1
        ? `?from=${fromLocations.slice(1).join(',')}`
        : ``;
    const path = routes[fromLocations[0]?.toLocaleLowerCase()];
    return path ? `${path}${fromId ? `/${fromId}` : ''}${params}` : baseUrl;
  };

  return { backPath: getBackPath() };
};

const getCatalogProductCurrencyPrices = (catalogCart) => {
  const minMaxCurrencyPrices = catalogCart?.ecomVendorProducts.reduce(
    (r, c) => {
      const productCurrencyType = (
        c.fkCurrencyTypeOverride || c.fkCurrencyType
      ).toString();

      const currencyPrice = r.currencyPrices.find(
        (f) => f.currencyType === productCurrencyType
      );
      if (currencyPrice) {
        currencyPrice.minPrice += c.minPrice / 100;
        currencyPrice.maxPrice += c.maxPrice / 100;
      }
      else {
        r.currencyPrices.push({
          currencyType: productCurrencyType,
          abbr:
            CURRENCY_TYPE_LIST.find(
              (f) => f.value.toString() === productCurrencyType
            )?.abbr || '',
          minPrice: c.minPrice / 100,
          maxPrice: c.maxPrice / 100
        });
      }
      return r;
    },
    { currencyPrices: [] }
  ) || { currencyPrices: [] };

  return { currencyPrices: minMaxCurrencyPrices.currencyPrices };
};

export {
  useProductSearchFilter,
  getVendorBranding,
  useCatalogBackPath,
  getCatalogProductCurrencyPrices
};
